import axios from "axios";
import NearbyLocationsRepo from "@repos/NearbyLocationsRepo";
import type { LocationData, SocialSetData } from "@contracts/socialLocations";
import type { NearbyImportResult, NearbySuggestion } from "@contracts/nearbyLocations";

export { isRemaining, isInReviewList, isAdditional } from "./NearbySuggestionSelection";

/** Adds to the review list, without importing. Bounded concurrency and failures retain each row's state. */
export async function addSuggestionsToReview(
  customerId: string,
  rows: NearbySuggestion[]
): Promise<{
  saved: NearbySuggestion[];
  failures: { suggestionId: string; message: string }[];
}> {
  const saved: NearbySuggestion[] = [];
  const failures: { suggestionId: string; message: string }[] = [];
  for (let offset = 0; offset < rows.length; offset += 4) {
    const batch = rows.slice(offset, offset + 4);
    const results = await Promise.allSettled(batch.map(row => NearbyLocationsRepo.update(customerId, row.id, { eTag: row._etag, reviewStatus: "Approved" })));
    results.forEach((result, index) => {
      if (result.status === "fulfilled") saved.push(result.value);
      else failures.push({ suggestionId: batch[index].id, message: nearbyErrorMessage(result.reason) });
    });
  }
  return { saved, failures };
}

/** The social accounts currently selected for a suggestion, in the shape a saved Location stores them. */
export function selectedSocialSet(row: NearbySuggestion): SocialSetData {
  const socialSet: SocialSetData = {};
  for (const candidate of row.excludeFromIndex.socialCandidates || []) {
    if (!candidate.selected) continue;
    if (candidate.network === "Instagram") {
      socialSet.instagramHandle = candidate.socialSet.instagramHandle;
      socialSet.instagramValidatedAt = candidate.socialSet.instagramValidatedAt;
    } else if (candidate.network === "Facebook") {
      socialSet.facebookPageId = candidate.socialSet.facebookPageId;
      socialSet.facebookPageName = candidate.socialSet.facebookPageName;
      socialSet.facebookPageUrl = candidate.socialSet.facebookPageUrl;
      socialSet.facebookValidatedAt = candidate.socialSet.facebookValidatedAt;
    } else if (candidate.network === "TikTok") socialSet.tikTokHandle = candidate.socialSet.tikTokHandle;
  }
  return socialSet;
}

/** A suggestion dressed as a Location so the shared Location display components can render it. */
export function previewLocation(row: NearbySuggestion): LocationData {
  return { id: row.locationId || row.id, customerId: row.customerId, name: row.displayName, socialSet: selectedSocialSet(row) };
}

export function hasSelectedSocials(row: NearbySuggestion): boolean {
  const socialSet = selectedSocialSet(row);
  return Boolean(socialSet.instagramHandle || socialSet.facebookPageName || socialSet.tikTokHandle);
}

export function nearbyErrorMessage(caught: unknown): string {
  if (axios.isAxiosError(caught)) {
    const data = caught.response?.data;
    return typeof data === "string" ? data : data?.title || data?.message || caught.message;
  }
  return caught instanceof Error ? caught.message : "The operation could not be completed.";
}

export interface NearbyImportMatches {
  locationMatches?: Record<string, string>;
  eventMatches?: Record<string, string>;
}

/**
 * Imports suggestions into saved Locations (and calendar events). Pending rows are approved by the server as
 * part of the import, so accepting a whole list is one request per 100 rows instead of one per suggestion.
 */
export async function importSuggestions(customerId: string, calendarId: string | null, rows: NearbySuggestion[], matches: NearbyImportMatches = {}): Promise<NearbyImportResult[]> {
  const results: NearbyImportResult[] = [];
  for (let offset = 0; offset < rows.length; offset += 100) {
    const items = rows.slice(offset, offset + 100).map(row => ({
      suggestionId: row.id,
      eTag: row._etag,
      existingLocationId: matches.locationMatches?.[row.id] || null,
      existingEventId: matches.eventMatches?.[row.id] || null,
    }));
    results.push(...(await NearbyLocationsRepo.import(customerId, calendarId, items, true)));
  }
  return results;
}

/** Results that did not end with a live Location (conflicts, failures, missing rows). */
export function failedImports(results: NearbyImportResult[]): NearbyImportResult[] {
  return results.filter(result => result.status !== "Created" && result.status !== "Linked");
}
